/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { findFiles, modifyFiles } from 'apis/admin/FileUpload';
import { FileInfo } from 'models/admin/FileInfo';
import axios from 'axios';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { ContentGrid } from 'components/layouts/ContentGrid';
import { useMessageBar } from 'hooks/useMessageBar';
import { AgGridReact } from 'ag-grid-react';
import { FontColor } from 'ui/theme/Color';
import { css } from '@mui/material';
import { CustomInputText } from 'components/inputs/CustomInput';
import { GreyLineButton } from 'components/buttons/CustomButton';
import { CrudCode } from 'models/common/Edit';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from 'utils/ApiUtil';

interface FileGridProps {
  atchFileGrId: string;
  showAllDownload?: boolean;
  fileCount?: (count: number) => void;
}

const FileGrid = ({ atchFileGrId, showAllDownload, fileCount }: FileGridProps) => {
  const { t } = useTranslation();

  const { openMessageBar } = useMessageBar();
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [isLicense, setIsLicense] = useState<boolean>(false);

  const fileColumnDefs: ColDef[] = [
    {
      headerName: t('com.label.정렬순번', '정렬순번') ?? '',
      field: 'sortOrd',
      width: 120,
      cellRenderer: (params: ICellRendererParams) => (
        <CustomInputText
          id="sortOrd"
          className="fullWidth alignC"
          value={params.data.sortOrd || ''}
          onChange={(e) => {
            handleSortOrdChange(params.data.atchFileId, e.target.value);
          }}
        />
      ),
    },
    {
      headerName: t('com.label.파일명', '파일명') ?? '',
      field: 'ctrcPialDivsNm',
      flex: 1,
      cellRenderer: (params: ICellRendererParams) => (
        <Link
          to="#"
          className="link"
          onClick={async () => {
            const fileInfo = {
              atchFileGrId: params.data.atchFileGrId,
              atchFileId: params.data.atchFileId,
              atchFileNm: params.data.atchFileNm,
              optValCtn1: params.data?.optValCtn1 ?? '',
            } as FileInfo;
            handleDownloadFile(fileInfo);
          }}
        >
          {`${params.data.atchFileNm} [${params.data.atchFileSize} KB]`}
        </Link>
      ),
    },
    {
      headerName: String(t('com.label.전체 다운로드', '전체 다운로드')),
      width: 150,
      headerComponent: () =>
        showAllDownload !== false && isLicense !== true ? (
          <GreyLineButton className="small" onClick={downloadAllFiles}>
            {t('com.button.전체 다운로드', '전체 다운로드')}
          </GreyLineButton>
        ) : (
          <></>
        ),
    },
    {
      headerName: String(t('com.button.저장', '저장')),
      width: 105,
      headerComponent: () => (
        <GreyLineButton className="small" onClick={fileOrderSave}>
          {t('com.button.저장', '저장')}
        </GreyLineButton>
      ),
    },
  ];

  const handleSortOrdChange = (key, newValue) => {
    setFiles((prev) =>
      prev.map((file) =>
        file.atchFileId === key
          ? {
              ...file,
              crudKey: file.crudKey == CrudCode.CREATE ? CrudCode.CREATE : CrudCode.UPDATE,
              sortOrd: newValue,
            }
          : file
      )
    );
  };

  const downloadAllFiles = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/v1/file/download/all?atchFileGrId=${atchFileGrId}`,
        {
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', atchFileGrId + '.zip');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
    }
  };

  const handleDownloadFile = async (file: FileInfo) => {
    try {
      // optValCtn1 값이 있을 경우에만 optValCtn1 경로로 파일 다운로드
      let fileDownloadUrl = `${API_BASE_URL}/v1/file/download?atchFileGrId=${file.atchFileGrId}&atchFileId=${file.atchFileId}`;
      if (file?.optValCtn1) {
        fileDownloadUrl = file.optValCtn1;
      }

      const response = await axios.get(fileDownloadUrl, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.atchFileNm);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
    }
  };

  const fileOrderSave = async () => {
    const result = await modifyFiles(files);
    if (result) {
      openMessageBar({
        type: 'confirm',
        content: t('com.label.저장되었습니다.', '저장되었습니다.'),
      });
    }
  };

  useEffect(() => {
    if (atchFileGrId) {
      findFiles(atchFileGrId).then((result: FileInfo[]) => {
        setFiles(result);
        fileCount && fileCount(result.length);
        if (null != result[0]?.optValCtn1) setIsLicense(true); // optValCtn1 값이 있으면 showAllDownload 안보이도록
      });
    } else {
      setFiles([]);
      fileCount && fileCount(0);
    }
  }, [atchFileGrId]);

  return (
    <ContentGrid className="ag-theme-alpine" style={{ height: '150px' }}>
      <AgGridReact
        overlayNoRowsTemplate="No rows to show"
        rowData={files || []}
        columnDefs={fileColumnDefs}
      ></AgGridReact>
    </ContentGrid>
  );
};

export default FileGrid;

const style = {
  fileName: () => css`
    &:hover,
    &:focus {
      color: ${FontColor.Primary};
      text-decoration: underline;
    }
  `,
};
